import { useContext, useEffect, useRef } from 'react'
import { isAwaitingFirstSync, SyncContext } from '../contexts/SyncProvider'
import { getPlatformAPI } from '../lib/platform'
import { useDataSource } from '../lib/dataSource'
import { useAccountSource } from '../lib/accountSource'
import { useBackupBridge } from '../lib/backup/backupBridge'
import { packAutoBackup } from '../lib/backup/autoBackup'

/** Nicht in den Start hinein: Der erste Sync und das Laden der Seiten gehen vor. */
export const AUTO_BACKUP_START_MS = 30_000
export const AUTO_BACKUP_RETRY_MS = 60_000
/** Die App läuft oft tagelang in der Menüleiste; stündlich geprüft fällt der Tageswechsel nicht durch. */
export const AUTO_BACKUP_CHECK_MS = 60 * 60_000
const MAX_SYNC_WAITS = 10

/**
 * **Automatische Backups** (#158, Entscheidung 4): einmal am Tag, im Hauptfenster. Erst nach dem ersten
 * Sync — mit Geräte-Sync soll der Stand der Cloud mit drin sein. Ob heute schon eins liegt und wohin es
 * geht, weiß der Hauptprozess; mehrere Fenster schreiben so trotzdem nur eins.
 */
export function useAutoBackup() {
  const sync = useContext(SyncContext)
  const latest = useRef(sync)
  latest.current = sync
  const dataSource = useDataSource()
  const account = useAccountSource()
  const bridge = useBackupBridge()

  useEffect(() => {
    if (getPlatformAPI().platform !== 'electron' || !window.api?.invoke) return
    let cancelled = false
    let running = false
    let syncWaits = 0
    let retry: ReturnType<typeof setTimeout> | undefined

    const waitingForSync = () => {
      const context = latest.current
      return isAwaitingFirstSync(context) || (!!context?.isSyncEnabled && context.isOnline && context.syncState.status === 'syncing')
    }

    const attempt = async () => {
      if (cancelled || running) return
      if (waitingForSync() && syncWaits < MAX_SYNC_WAITS) {
        syncWaits++
        clearTimeout(retry)
        retry = setTimeout(() => void attempt(), AUTO_BACKUP_RETRY_MS)
        return
      }
      syncWaits = 0
      running = true
      try {
        const status = await bridge.autoStatus()
        if (cancelled || !status.enabled || status.hasToday) return
        const accountId = await account.getUserId().catch(() => null)
        const bytes = await packAutoBackup(dataSource, accountId, bridge)
        if (bytes && !cancelled) await bridge.autoWrite(bytes)
      } catch (error) {
        console.error('[Backup] Automatisches Backup gescheitert:', error)
      } finally {
        running = false
      }
    }

    const start = setTimeout(() => void attempt(), AUTO_BACKUP_START_MS)
    const hourly = setInterval(() => void attempt(), AUTO_BACKUP_CHECK_MS)
    return () => {
      cancelled = true
      clearTimeout(start)
      clearTimeout(retry)
      clearInterval(hourly)
    }
  }, [account, bridge, dataSource])
}
